import React from 'react';
import { StyleSheet } from 'react-native';
import { Button as PaperButton } from 'react-native-paper';

import { handleAuthError } from '@auth/AuthErrorHandler';
import { signOut } from '@auth/ManageUser';
import { usePaperTheme } from '@context/ThemeContext';
import { chooseByStatus } from '@db/Requests/Common';
import { RequestStatus } from '@types';

export type ButtonProps = React.ComponentProps<typeof PaperButton>;

export const bigButtonStyles = StyleSheet.create({
  button: {
    marginVertical: 8,
    paddingVertical: 2,
    width: '100%',
  },
  text: {
    fontSize: 14,
    fontWeight: 'bold',
    lineHeight: 20,
  },
});

export function Button(props: ButtonProps) {
  return <PaperButton {...props} />;
}

export function BigButton({ mode, style, labelStyle, ...props }: ButtonProps) {
  return (
    <Button
      style={[bigButtonStyles.button, style]}
      labelStyle={[bigButtonStyles.text, labelStyle]}
      mode={mode}
      {...props}
    />
  );
}

export function BigTextInputButton({ style, labelStyle, ...props }: ButtonProps) {
  const paperTheme = usePaperTheme();

  const dynamicStyles = StyleSheet.create({
    button: {
      backgroundColor: paperTheme.colors.background,
      borderColor: paperTheme.colors.placeholder,
      borderWidth: 1,
    },
    label: { color: paperTheme.colors.text },
  });

  return (
    <BigButton
      style={[dynamicStyles.button, style]}
      labelStyle={[dynamicStyles.label, labelStyle]}
      {...props}
    />
  );
}

export type StatusButtonProps = Omit<ButtonProps, 'children'> & {
  status: RequestStatus;
  text: {
    accepted: string;
    pending: string;
    undetermined: string;
  };
};

export function ButtonWithStatus({ status, text, ...props }: StatusButtonProps) {
  const paperTheme = usePaperTheme();

  return (
    <Button
      mode={chooseByStatus(status, {
        [RequestStatus.ACCEPTED]: 'outlined',
        [RequestStatus.PENDING]: 'outlined',
        [RequestStatus.REJECTED]: 'outlined',
        [RequestStatus.UNDETERMINED]: 'contained',
      })}
      textColor={chooseByStatus(status, {
        [RequestStatus.ACCEPTED]: paperTheme.colors.placeholder,
        [RequestStatus.PENDING]: paperTheme.colors.placeholder,
        [RequestStatus.REJECTED]: paperTheme.colors.placeholder,
        [RequestStatus.UNDETERMINED]: undefined,
      })}
      {...props}>
      {chooseByStatus(status, {
        [RequestStatus.ACCEPTED]: text.accepted,
        [RequestStatus.PENDING]: text.pending,
        [RequestStatus.REJECTED]: text.pending,
        [RequestStatus.UNDETERMINED]: text.undetermined,
      })}
    </Button>
  );
}

export function WhiteButtonWithStatus({ status, text, ...props }: StatusButtonProps) {
  const paperTheme = usePaperTheme();

  return (
    <ButtonWithStatus
      status={status}
      mode="contained"
      buttonColor={chooseByStatus(status, {
        [RequestStatus.ACCEPTED]: paperTheme.colors.disabled,
        [RequestStatus.PENDING]: paperTheme.colors.disabled,
        [RequestStatus.REJECTED]: paperTheme.colors.disabled,
        [RequestStatus.UNDETERMINED]: 'white',
      })}
      text={text}
      {...props}
    />
  );
}

export function LogoutButton(props: Omit<ButtonProps, 'children'>) {
  const onLogoutPressed = async () => {
    try {
      await signOut();
    } catch (error) {
      handleAuthError(error);
    }
  };

  return (
    <BigButton icon="logout" mode="outlined" onPress={onLogoutPressed} {...props}>
      Logout
    </BigButton>
  );
}
